import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

import { CampaignDto, apiService } from '@shared/services/apiService/apiService';
import { useAppDispatch } from '@shared/store';
import { resolveSocketIoConnection } from '@shared/utils/serviceUrl';

const { url: WS_URL, path: WS_PATH } = resolveSocketIoConnection(import.meta.env.VITE_API_URL);

interface UseCampaignRealtimeSyncOptions {
  campaignId?: string;
  companyId?: string;
}

export const useCampaignRealtimeSync = ({
  campaignId,
  companyId,
}: UseCampaignRealtimeSyncOptions) => {
  const dispatch = useAppDispatch();
  const campaignSocketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io(`${WS_URL}/campaigns`, {
      path: `${WS_PATH}/socket.io`,
      transports: ['websocket'],
      autoConnect: true,
      auth: { token },
    });
    campaignSocketRef.current = socket;

    return () => {
      socket.disconnect();
      campaignSocketRef.current = null;
    };
  }, []);

  useEffect(() => {
    const socket = campaignSocketRef.current;
    if (!socket || !companyId) return;

    const subscribeToCampaignsList = () => {
      socket.emit('subscribe_to_get_campaigns_list', {
        companyId,
      });
    };

    if (socket.connected) {
      subscribeToCampaignsList();
    }
    socket.on('connect', subscribeToCampaignsList);

    return () => {
      socket.off('connect', subscribeToCampaignsList);
    };
  }, [companyId]);

  useEffect(() => {
    const socket = campaignSocketRef.current;
    if (!socket || !campaignId) return;

    const onCampaignUpdate = (value: { result: CampaignDto }) => {
      const updatedCampaign = value?.result;
      if (!updatedCampaign || updatedCampaign.id !== campaignId) return;

      dispatch(
        apiService.util.updateQueryData(
          'campaignControllerCampaign',
          { id: campaignId },
          (draft) => {
            if (draft) {
              draft.status = updatedCampaign.status;
            }
          },
        ),
      );
    };

    socket.on('get_campaigns_list', onCampaignUpdate);

    return () => {
      socket.off('get_campaigns_list', onCampaignUpdate);
    };
  }, [campaignId, dispatch]);
};
